import { KeyRoundIcon, ShieldAlertIcon } from 'lucide-react'

import { CopyButton } from '@/components/CopyButton'
import { cn } from '@/lib/utils'

interface SecretTokenPanelProps {
  token: string
  mcpUrl?: string
  className?: string
}

/**
 * The one-time reveal of a raw bearer token.
 *
 * ARCHITECTURE.md §7: "The raw bearer token is shown exactly once". The warning
 * is deliberately the loudest thing on the panel — there is no recovery path,
 * only rotation.
 */
export function SecretTokenPanel({ token, mcpUrl, className }: SecretTokenPanelProps) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border-2 border-[color-mix(in_oklch,var(--destructive)_45%,var(--border))]',
        className,
      )}
    >
      <div className="flex items-start gap-3 bg-[color-mix(in_oklch,var(--destructive)_10%,var(--card))] px-5 py-4">
        <ShieldAlertIcon className="mt-0.5 size-5 shrink-0 text-destructive" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">
            This is the only time you will see this token.
          </p>
          <p className="max-w-prose text-sm text-pretty text-muted-foreground">
            saltseed stores only a SHA-256 hash of it. Copy it into your MCP client
            configuration now. If you lose it, the only way forward is to rotate the token,
            which immediately invalidates every client still using the old one.
          </p>
        </div>
      </div>

      <div className="space-y-4 bg-card px-5 py-5">
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
            <KeyRoundIcon className="size-3.5" />
            Bearer token
          </div>
          <div className="flex items-start gap-2 rounded-lg border border-border bg-[var(--surface)] p-3">
            <code className="min-w-0 flex-1 font-mono text-[0.8125rem] leading-relaxed break-all text-foreground">
              {token}
            </code>
            <CopyButton value={token} label="Copy bearer token" variant="outline" withText />
          </div>
        </div>

        {mcpUrl === undefined ? null : (
          <div className="space-y-1.5">
            <div className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
              MCP endpoint
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-[var(--surface)] p-3">
              <code className="min-w-0 flex-1 font-mono text-[0.8125rem] break-all text-foreground">
                {mcpUrl}
              </code>
              <CopyButton value={mcpUrl} label="Copy MCP URL" variant="outline" />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
